export const ImportSitemapResponseTypeUrlset = 'urlset'
export const ImportSitemapResponseTypeSitemapIndex = 'sitemapindex'

// A page block the crawler inferred from the markup (OCT-4990). `wireframes`
// holds wireframe ids as the catalog names them; the front drops ids it does
// not know. `color` is a palette name (`purple`, `topaz`, ...): a semantic
// tint by section type, not the site's own colours.
export interface CrawledBlockFields {
  title: string
  wireframes: string[]
}

export interface CrawledBlockColor {
  color: string | null
}

// The section's text as markdown, sent only when the crawl asked for block
// content (OCT-5038). Null or missing: a block without a body.
export interface CrawledBlockContent {
  content: string | null
}

export type CrawledBlock = Partial<CrawledBlockColor & CrawledBlockContent> &
  CrawledBlockFields

// What an imported page is named after: the last segment of its URL (the default),
// its <title> or its first <h1> (OCT-5509). A page without the chosen field keeps
// its URL name.
export type PageNameSource = 'url' | 'title' | 'h1'

export type ImportSitemapResultUrlItem = {
  url: string
  title: string | null
  description: string | null
  h1: string | null
  // Sent only by a crawler that reports each page's own outcome (OCT-4848).
  // The sitemap report reads them; the visual import ignores them.
  status?: number | null
  noindex?: boolean
  canonical?: string | null
  // Sent only with the `done` event of a crawl started with blocks (OCT-4990).
  // An array, even an empty one, means the page was matched: the visual import
  // then builds these blocks instead of the title/description/h1 text blocks.
  // A page without the field falls back to the text blocks.
  blocks?: CrawledBlock[]
}

// A URL the crawl saw that did not become a page: it answered with an error,
// was skipped for a named reason, or redirected somewhere else.
export type CrawlerIssueItem = {
  url: string
  status?: number | null
  skipReason?: string | null
  redirectTarget?: string | null
}

export type ImportSitemapData<AdditionalData extends {} = {}> = {
  type:
    | typeof ImportSitemapResponseTypeUrlset
    | typeof ImportSitemapResponseTypeSitemapIndex
  data: (ImportSitemapResultUrlItem & AdditionalData)[]
  timeout: boolean
  extra: CrawlerExtra
  limitExceeded: boolean
  details: CrawlerDetails
  issues?: CrawlerIssueItem[]
  // The address the visitor asked to crawl. Page urls only give an origin, which
  // loses the path when a subfolder was crawled.
  siteUrl?: string
}

export interface CrawlerCounters {
  countSkippedByTimeout: number
  countFailed: number
  countSizeExceeded: number
  countAbsent: number
  countDenied: number
}

// Sent only by a crawler that knows the failure reasons.
export interface CrawlerReasonCounters {
  countDomainNotFound: number
}

export type CrawlerDetails = Partial<CrawlerReasonCounters> & CrawlerCounters

// The crawler keeps adding codes, so an unrecognized reason falls back to the message.
export type CrawlerFailureReason =
  | 'domain_not_found'
  | 'crawler_blocked'
  | 'unknown'

export interface CrawlerErrorPayload {
  details: CrawlerDetails
}

// Sent only by a crawler that knows the failure reasons.
export interface CrawlerErrorReason {
  reason: CrawlerFailureReason
}

export interface CrawlerErrorMessage {
  message: string
  data: CrawlerErrorPayload
}

export type CrawlerErrorData = Partial<CrawlerErrorReason> & CrawlerErrorMessage

export type CrawlerExtra = Omit<ImportSitemapResultUrlItem, 'url'> & {
  icon: string
}

// Sent once with the last progress of a crawl with blocks (OCT-5038): every
// page is in, `done` waits for the model's last batch.
export type CrawlerStage = 'blocks'

export type CrawlerProgress = {
  id?: string
  progressPercentage: number
  scanned: number
  totalCount: number
  url: string
  limitExceeded: boolean
  timeoutExceeded: boolean
  resultPage?: ImportSitemapResultUrlItem
  extra?: CrawlerExtra
  details: CrawlerDetails
  stage?: CrawlerStage
}

export interface CrawlerRedirect {
  sourceUrl: string
  destinationUrl: string
}

export type CrawlerJobId = string
